import { Trans, useTranslation } from 'react-i18next'

import LangLink from '../components/LangLink'
import Section from '../components/Section'

interface Step {
  title: string
  body: string
}

const DETAIL_KEYS = [
  'legalName',
  'legalForm',
  'owner',
  'vat',
  'enterprise',
  'address',
  'website',
] as const

function AboutPage() {
  const { t } = useTranslation()
  const process = t('about.process', { returnObjects: true }) as Step[]
  const d = 'about.details'

  return (
    <div className="about-page">
      {/* ---------- header ---------- */}
      <Section background="surface" space="md" className="about-hero">
        <p className="eyebrow">{t('about.eyebrow')}</p>
        <h1 className="display">{t('about.title')}</h1>
        <p className="about-lead">{t('about.intro')}</p>
      </Section>

      {/* ---------- the person: editorial two-column ---------- */}
      <Section background="beige" space="lg" className="about-person">
        <div className="about-person-grid">
          <picture>
            <source srcSet="/about/emile.webp" type="image/webp" />
            <img
              src="/about/emile.jpg"
              alt={t('about.photoAlt')}
              className="about-portrait"
              loading="lazy"
              width={900}
              height={1125}
            />
          </picture>
          <div className="about-person-text">
            <h2>{t('about.personHeading')}</h2>
            <p>{t('about.bio1')}</p>
            <p>{t('about.bio2')}</p>
            <p>{t('about.bio3')}</p>
          </div>
        </div>
      </Section>

      {/* ---------- company details: dark "at a glance" band ---------- */}
      <Section background="dark" space="lg" className="about-details">
        <h2>{t('about.companyDetailsHeading')}</h2>
        <dl className="spec-list about-spec">
          {DETAIL_KEYS.map((key) => (
            <div key={key}>
              <dt>{t(`${d}.${key}`)}</dt>
              <dd>{t(`${d}.${key}Value`)}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* ---------- how we work: condensed indexed steps ---------- */}
      <Section background="surface" space="lg" className="about-process">
        <h2>{t('about.howWeWorkHeading')}</h2>
        <ol className="about-steps">
          {process.map((step, i) => (
            <li key={step.title}>
              <span className="about-step-index">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
        <p className="about-outro">
          <Trans
            i18nKey="about.outro"
            components={{ lnk: <LangLink to="/how-to-order" /> }}
          />
        </p>
      </Section>

      {/* ---------- quote CTA band ---------- */}
      <Section background="bordeaux" space="md" className="about-cta">
        <h2>{t('home.quote.heading')}</h2>
        <p>{t('home.quote.body')}</p>
        <LangLink to="/how-to-order" className="btn btn--on-dark">
          {t('home.cta')}
        </LangLink>
      </Section>
    </div>
  )
}

export default AboutPage
